// The issuing page's view state, and its spelling in the query string.
//
// The URL is the state, not a copy of it: the page reads what it renders from
// `useSearchParams` and writes every change straight back. A shared link is
// therefore the view somebody was looking at, and there is no second copy in a
// `useState` to fall out of step with it.
//
// Two things are deliberately NOT stored:
//
//   - The resolved window. `period=month` means "this month", which is a
//     different range tomorrow; storing the dates it resolved to today would
//     freeze a link that was meant to stay current. `resolveWindow` derives them.
//   - The granularity, when the user hasn't chosen one. `bucket` absent means
//     "follow the range", which is a real state and not a synonym for `day` — it
//     is what lets the granularity track a period change until the moment the
//     user overrides it, and stay put afterwards.
//
// Everything read out of the query string is validated against the vocabulary it
// belongs to. A hand-edited URL is untrusted input, and the failure mode of
// passing it through is a 400 the user can neither see nor fix.

import { BUCKET_GRANULARITIES, MEASURE_IDS } from '@/lib/analytics';
import type { BucketGranularity, MeasureId } from '@/lib/analytics';
import { isKnownItemType } from '@/lib/itemTypeConfig';
import type { ItemType } from '@/lib/itemTypeConfig';
import {
  PERIOD_PRESETS,
  clampRange,
  presetRange,
  rangeLengthInDays,
  resolveGranularity,
} from './issuingWindow';
import type { CalendarRange, PeriodPreset, ResolvedWindow } from './issuingWindow';

export interface IssuingView {
  readonly period: PeriodPreset;
  /** Raw form values; only consulted when `period` is `custom`. */
  readonly customFrom: string;
  readonly customTo: string;
  /** `null` means "follow the range" — see the note at the top of this file. */
  readonly bucket: BucketGranularity | null;
  readonly measure: MeasureId;
  readonly itemTypes: readonly ItemType[];
  readonly itemId: string | null;
}

export const DEFAULT_PERIOD: PeriodPreset = 'last30';
export const DEFAULT_MEASURE: MeasureId = 'units';

/** The range a `custom` view falls back to when its dates are missing or bad. */
export const FALLBACK_PERIOD: Exclude<PeriodPreset, 'custom'> = 'last30';

const PARAM = {
  period: 'period',
  from: 'from',
  to: 'to',
  bucket: 'bucket',
  measure: 'measure',
  itemTypes: 'types',
  itemId: 'item',
} as const;

function oneOf<T extends string>(values: readonly T[], raw: string | null): T | null {
  return raw !== null && (values as readonly string[]).includes(raw) ? (raw as T) : null;
}

export function readView(params: URLSearchParams): IssuingView {
  const itemId = params.get(PARAM.itemId)?.trim();
  return {
    period: oneOf(PERIOD_PRESETS, params.get(PARAM.period)) ?? DEFAULT_PERIOD,
    customFrom: params.get(PARAM.from)?.trim() ?? '',
    customTo: params.get(PARAM.to)?.trim() ?? '',
    bucket: oneOf(BUCKET_GRANULARITIES, params.get(PARAM.bucket)),
    measure: oneOf(MEASURE_IDS, params.get(PARAM.measure)) ?? DEFAULT_MEASURE,
    itemTypes: (params.get(PARAM.itemTypes) ?? '')
      .split(',')
      .map((part) => part.trim())
      .filter(isKnownItemType),
    itemId: itemId ? itemId : null,
  };
}

/**
 * The query string for a view. Anything at its default is omitted, so a plain
 * `/analytics/issuing` is the default view and every parameter present in a URL
 * is one somebody chose.
 */
export function writeView(view: IssuingView): URLSearchParams {
  const params = new URLSearchParams();
  if (view.period !== DEFAULT_PERIOD) params.set(PARAM.period, view.period);
  if (view.period === 'custom') {
    params.set(PARAM.from, view.customFrom);
    params.set(PARAM.to, view.customTo);
  }
  if (view.bucket !== null) params.set(PARAM.bucket, view.bucket);
  if (view.measure !== DEFAULT_MEASURE) params.set(PARAM.measure, view.measure);
  if (view.itemTypes.length > 0) params.set(PARAM.itemTypes, view.itemTypes.join(','));
  if (view.itemId !== null) params.set(PARAM.itemId, view.itemId);
  return params;
}

/** The dates a view means today, and the granularity to draw them at. */
export function resolveWindow(view: IssuingView, today: string): ResolvedWindow {
  const fallback = presetRange(FALLBACK_PERIOD, today);
  const range: CalendarRange =
    view.period === 'custom'
      ? clampRange({ from: view.customFrom, to: view.customTo }, fallback)
      : presetRange(view.period, today);

  return {
    ...range,
    bucket: resolveGranularity(range, view.bucket),
    days: rangeLengthInDays(range),
    includesToday: range.from <= today && today <= range.to,
  };
}
